import { Loader2, Send } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { apiErrorMessage } from '@/i18n'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDateTime } from '../shared/format'
import { useCreateTicketMessage, useTicketMessages } from './api'

type Props = { ticketId: string; accountId?: string }

export function MessagesPanel({ ticketId, accountId }: Props) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const messages = useTicketMessages(ticketId)
  const create = useCreateTicketMessage(ticketId)
  const [body, setBody] = useState('')
  const canWrite = can('messages.write')

  const send = async () => {
    const text = body.trim()
    if (!text) return
    try {
      await create.mutateAsync({ body: text, account_id: accountId })
      setBody('')
      toast.success(t('common.saved'))
    } catch (e) {
      toast.error(apiErrorMessage(e))
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b px-4 py-3">
        <h3 className="text-sm font-medium">{t('tickets.messagesTitle')}</h3>
      </div>
      <div className="flex-1 space-y-3 overflow-auto scrollbar-none p-4">
        {messages.isError ? (
          <ErrorState error={messages.error} onRetry={() => void messages.refetch()} />
        ) : messages.isLoading ? (
          Array.from({ length: 3 }, (_, i) => <Skeleton key={i} className="h-16" />)
        ) : (messages.data ?? []).length === 0 ? (
          <EmptyState title={t('tickets.noMessages')} />
        ) : (
          (messages.data ?? []).map((m) => (
            <div key={m.id} className="rounded-lg border bg-card p-3">
              <div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="font-mono">{m.author_user_id.slice(0, 8)}</span>
                <span className="tabular-nums">{fmtDateTime(m.created_at)}</span>
              </div>
              <p className="text-sm whitespace-pre-wrap">{m.body}</p>
            </div>
          ))
        )}
      </div>
      {canWrite ? (
        <div className="flex gap-2 border-t p-3">
          <Input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('tickets.messagePlaceholder')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void send()
              }
            }}
            aria-label={t('tickets.messagePlaceholder')}
          />
          <Button onClick={() => void send()} disabled={create.isPending || !body.trim()} aria-label={t('tickets.send')}>
            {create.isPending ? <Loader2 className="animate-spin" /> : <Send />}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
